import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { usePmProperty } from '@/hooks/usePmProperty';
import {
  getAnalyticsSummary,
  getGateTraffic,
  getVisitorTypeBreakdown,
  getHeatmap,
} from '@/services/api.client';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PIE_COLOURS = [
  'var(--status-green)',
  'var(--status-amber)',
  'var(--status-red)',
  'var(--status-blue)',
  'var(--status-purple)',
];

type Range = '7d' | '30d' | '90d';

function rangeParams(range: Range): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  return {
    dateFrom: format(subDays(now, days), 'yyyy-MM-dd'),
    dateTo: format(now, 'yyyy-MM-dd'),
  };
}

export function AnalyticsPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const [range, setRange] = useState<Range>('30d');
  const params = rangeParams(range);

  const summaryQuery = useQuery({
    queryKey: ['pm', 'analytics', 'summary', propertyId, range],
    queryFn: () => getAnalyticsSummary(propertyId, params),
    enabled: !!propertyId,
  });

  const trafficQuery = useQuery({
    queryKey: ['pm', 'analytics', 'traffic', propertyId, range],
    queryFn: () => getGateTraffic(propertyId, params),
    enabled: !!propertyId,
  });

  const typesQuery = useQuery({
    queryKey: ['pm', 'analytics', 'types', propertyId, range],
    queryFn: () => getVisitorTypeBreakdown(propertyId, params),
    enabled: !!propertyId,
  });

  const heatmapQuery = useQuery({
    queryKey: ['pm', 'analytics', 'heatmap', propertyId, range],
    queryFn: () => getHeatmap(propertyId, params),
    enabled: !!propertyId,
  });

  const summary = summaryQuery.data;
  const heatmap = heatmapQuery.data ?? [];

  // Build lookup: heatmap[day][hour] → count
  const heatmapLookup: Record<number, Record<number, number>> = {};
  for (const cell of heatmap) {
    heatmapLookup[cell.day] ??= {};
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    heatmapLookup[cell.day]![cell.hour] = cell.count;
  }
  const maxCount = Math.max(1, ...heatmap.map((c) => c.count));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl">Analytics</h1>
        <div className="flex gap-2">
          {(['7d', '30d', '90d'] as Range[]).map((r) => (
            <Button
              key={r}
              size="sm"
              variant={range === r ? 'default' : 'outline'}
              onClick={() => setRange(r)}
            >
              {r === '7d' ? 'Last 7 days' : r === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary stat cards */}
      {summaryQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { label: 'Total Visits', value: summary?.totalVisits.toLocaleString() ?? '—' },
            { label: 'Peak Hour', value: summary?.peakHour ?? '—' },
            { label: 'Busiest Gate', value: summary?.busiestGate ?? '—' },
            { label: 'Busiest Day', value: summary?.busiestDayOfWeek ?? '—' },
            {
              label: 'Walk-In Rate',
              value: summary ? `${Math.round(summary.walkInRate * 100)}%` : '—',
            },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4"
            >
              <p className="text-xs text-[var(--text-tertiary)]">{label}</p>
              <p className="mt-1 text-xl font-semibold">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Gate Traffic bar chart */}
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4 space-y-3">
          <p className="font-medium text-sm">Gate Traffic</p>
          {trafficQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={trafficQuery.data ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                {/* Derive gate names dynamically from the first data point so
                    the chart works on any property regardless of gate names. */}
                {Object.keys(trafficQuery.data?.[0] ?? {})
                  .filter((k) => k !== 'period')
                  .map((gateName, idx) => (
                    <Bar
                      key={gateName}
                      dataKey={gateName}
                      fill={PIE_COLOURS[idx % PIE_COLOURS.length] ?? '#F4A261'}
                      radius={[2, 2, 0, 0]}
                    />
                  ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Visitor Type pie chart */}
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4 space-y-3">
          <p className="font-medium text-sm">Visitor Types</p>
          {typesQuery.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={typesQuery.data ?? []}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                >
                  {(typesQuery.data ?? []).map((_, idx) => (
                    <Cell key={idx} fill={PIE_COLOURS[idx % PIE_COLOURS.length] ?? '#F4A261'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Heatmap */}
      <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4 space-y-3">
        <p className="font-medium text-sm">Peak Access Hours</p>
        {heatmapQuery.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="overflow-x-auto">
            <div
              className="inline-grid gap-px"
              style={{ gridTemplateColumns: `40px repeat(24, 20px)` }}
            >
              {/* Header row: hours */}
              <div />
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="text-center text-[9px] text-[var(--text-tertiary)] leading-5"
                >
                  {h % 3 === 0 ? `${h}h` : ''}
                </div>
              ))}
              {/* Data rows: one per day */}
              {DAYS.map((day, dayIdx) => (
                <React.Fragment key={dayIdx}>
                  <div className="text-[10px] text-[var(--text-tertiary)] flex items-center">
                    {day}
                  </div>
                  {HOURS.map((hour) => {
                    const count = heatmapLookup[dayIdx]?.[hour] ?? 0;
                    const opacity = count > 0 ? 0.2 + (count / maxCount) * 0.8 : 0;
                    return (
                      <div
                        key={`${dayIdx}-${hour}`}
                        className="h-5 w-5 rounded-sm"
                        style={{ backgroundColor: `rgba(244,162,97,${opacity})` }}
                        title={`${day} ${hour}:00 — ${count} visits`}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
